// @ts-nocheck
import logger from "@insite/client-framework/Logger";
import { UploadedItem } from "@insite/client-framework/Store/Components/OrderUpload/OrderUploadState";
import { useState } from "react";
import { WorkBook } from "xlsx";

export interface ProcessFileParameter {
    data: ArrayBuffer;
    fileExtension: string;
}

export interface ProcessFileResult {
    uploadedItems: UploadedItem[];
    uploadLimitExceeded: boolean;
    isBadFile: boolean;
    isUploading: boolean;
    allowCancel: boolean;
}
const defaultProcessFileResult: ProcessFileResult = {
    uploadedItems: [],
    uploadLimitExceeded: false,
    isBadFile: false,
    isUploading: false,
    allowCancel: false,
};

type ProcessCSV = (csv: string, result: ProcessFileResult) => Promise<any>;
type ProcessXLSX = (workBook: WorkBook, result: ProcessFileResult) => Promise<any>;

type HandleResult = (result: ProcessFileResult) => void;
const firstRowHeaderText = "number";
const useProcessFile = (handleResult: HandleResult | undefined = undefined) => {
    const [defaultResults, setDefaultResults] = useState<ProcessFileResult>(defaultProcessFileResult);
    const setResults = handleResult || setDefaultResults;

    const fixData = (data: ArrayBuffer) => {
        let dataAsString = "";
        let l = 0;
        const w = 10240;
        for (; l < data.byteLength / w; ++l) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            dataAsString += String.fromCharCode.apply(null, new Uint8Array(data.slice(l * w, l * w + w)) as any);
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dataAsString += String.fromCharCode.apply(null, new Uint8Array(data.slice(l * w)) as any);

        return dataAsString;
    };

    const processExcelWorkBook = async (workBook: WorkBook, result: ProcessFileResult) => {
        const XLSX = await import(/* webpackChunkName: "xlsx" */ "xlsx");

        workBook.SheetNames.forEach((sheetName: string) => {
            const opts = { header: 1 };
            let rows = XLSX.utils.sheet_to_json<string[]>(workBook.Sheets[sheetName], opts);

            if (rows.length > 0) {
                rows = rows.slice(1, rows.length);
                rows = rows.filter(row => {
                    if (typeof row[0] === "number") {
                        row[0] = String(row[0]);
                    }
                    return row[0] != null && row[0].length > 0;
                });

                if (limitExceeded(rows.length)) {
                    setResults({ ...result, uploadLimitExceeded: true });
                }

                result.uploadedItems = rows.map(row => ({
                    name: row[0],
                    qtyOrdered: parseFloat(row[1]) ? parseFloat(row[1]) : undefined,
                    unitOfMeasure: row[2],
                }));
            }
        });

        if (!result.isBadFile && !result.uploadLimitExceeded) {
            result.allowCancel = true;
            result.isUploading = true;
        }

        return result;
    };

    const limitExceeded = (rowsCount: number) => {
        const maximumAllowedRowsCount = 500;
        return rowsCount > maximumAllowedRowsCount;
    };

    const processCsv = async (data: string, result: ProcessFileResult) => {
        const { parse } = await import(/* webpackChunkName: "papaparse" */ "papaparse");
        const newLineIndex = data.lastIndexOf("\r\n");
        if (newLineIndex + 2 === data.length) {
            data = data.substring(0, newLineIndex);
        }

        const results = parse<(string | null)[]>(data);
        if (results.errors.length > 0) {
            result.isBadFile = true;
            return;
        }

        const rows = results.data.slice(1, results.data.length);

        if (limitExceeded(rows.length)) {
            return { ...result, uploadLimitExceeded: true };
        }

        rows.forEach((s: (string | null)[]) => {
            if (s[0] == null || s[0].length === 0) {
                return false;
            }

            const objectToAdd: UploadedItem = {
                name: s[0],
            };
            if (s[1]) {
                objectToAdd.qtyOrdered = parseFloat(s[1]) ? parseFloat(s[1]) : undefined;
            }

            if (s[2]) {
                objectToAdd.unitOfMeasure = s[2];
            }

            result.uploadedItems.push(objectToAdd);
        });
        return result;
    };
    const processFile = async (
        parameter: ProcessFileParameter,
        processCSVCallback: ProcessCSV = processCsv,
        processWorkBookCallback: ProcessXLSX = processExcelWorkBook,
    ) => {
        const newResult = { ...defaultProcessFileResult };

        const { data, fileExtension } = parameter;
        const fixedData = fixData(data);

        try {
            if (fileExtension === "csv") {
                await processCSVCallback(fixedData, newResult);
            } else {
                const { read } = await import(/* webpackChunkName: "xlsx" */ "xlsx");
                const workBook = read(btoa(fixedData), { type: "base64" });
                if (workBook) {
                    await processWorkBookCallback(workBook, newResult);
                }
            }
        } catch (error) {
            logger.error(error);
            setResults({ ...newResult, isBadFile: true });
        }

        if (!newResult.isBadFile && !newResult.uploadLimitExceeded) {
            newResult.allowCancel = true;
            newResult.isUploading = true;
        }
        setResults(newResult);
        return newResult;
    };

    return {
        results: defaultResults,
        setResults: setDefaultResults,
        processFile,
        processCsv,
        processWorkBook: processExcelWorkBook,
        limitExceeded,
    };
};

export default useProcessFile;
